import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { hashKey, QueryClient, QueryClientProvider, QueryObserver, useMutation, useQueries, useQuery, useQueryClient, type Query } from '@tanstack/react-query';
import type { Language } from './crs';
import type { AccountQueryObserver } from './account-query-contract';
export type { AccountQueryObserver } from './account-query-contract';

type QueryIdentity = { owner: string | null; epoch: number; privateEnabled: boolean; deletionPending?: boolean };
const IdentityContext = createContext<(QueryIdentity & { isCurrent: (scope: string) => boolean }) | null>(null);
const identityKey = ({ owner, epoch, privateEnabled, deletionPending = false }: QueryIdentity) => ['account', owner, epoch, privateEnabled, deletionPending] as const;

/** Query owns request coordination; authentication remains a fresh server check. */
export function AccountQueryClientProvider({ children }: { children: ReactNode }) {
  const [client] = useState(() => new QueryClient({ defaultOptions: {
    queries: { staleTime: 0, gcTime: 60000, retry: false, refetchOnReconnect: true },
    mutations: { retry: false, gcTime: 60000 },
  } }));
  return <QueryClientProvider client={client}>{children}</QueryClientProvider>;
}

export function AccountQueryProvider({ children, ...identity }: QueryIdentity & { children: ReactNode }) {
  return <AccountQueryClientProvider><AccountQueryScope {...identity}>{children}</AccountQueryScope></AccountQueryClientProvider>;
}

/** Controls can read within the same client before supplying business admission. */
export function AccountQueryScope({ owner, epoch, privateEnabled = true, deletionPending = false, children }: QueryIdentity & { children: ReactNode }) {
  const client = useQueryClient();
  const identity = { owner, epoch, privateEnabled, deletionPending };
  const scope = hashKey(identityKey(identity)), current = useRef<string | null>(scope);
  current.current = scope;
  const isCurrent = useCallback((expected: string) => current.current === expected, []);
  useLayoutEffect(() => {
    current.current = scope;
    const retired = identityKey(identity);
    return () => {
      if (current.current === scope) current.current = null;
      void client.cancelQueries({ queryKey: retired });
      // Observer cleanup follows the parent layout cleanup. Remove afterward,
      // so its final unsubscribe cannot reschedule a retired query's GC timer.
      queueMicrotask(() => {
        // StrictMode replays setup for this same scope before the microtask.
        // Its new observers must keep their active cache entry and request.
        if (current.current === scope) return;
        client.removeQueries({ queryKey: retired });
        for (const mutation of client.getMutationCache().findAll({ mutationKey: retired })) { mutation.destroy(); client.getMutationCache().remove(mutation); }
      });
    };
  }, [client, scope]);
  return <IdentityContext.Provider value={{ ...identity, isCurrent }}>{children}</IdentityContext.Provider>;
}

function useIdentity() {
  const identity = useContext(IdentityContext);
  if (!identity) throw new Error('Account query provider is missing');
  return identity;
}

export function errorText(error: unknown, language: Language): string {
  return error && typeof error === 'object' && 'messageFor' in error && typeof error.messageFor === 'function'
    ? error.messageFor(language) : error instanceof Error ? error.message
    : language === 'zh' ? '暂时无法完成，请重试。' : 'Unable to complete this action. Please retry.';
}
const errorCode = (error: unknown): string | undefined => error && typeof error === 'object' && 'code' in error && typeof error.code === 'string' ? error.code : undefined;
const errorStatus = (error: unknown): number | undefined => error && typeof error === 'object' && 'status' in error && typeof error.status === 'number' ? error.status : undefined;
const denied = (error: unknown) => [401,403,428].includes(errorStatus(error) ?? 0) || ['unauthorized','forbidden','policy_acknowledgement_required','consent_required','account_deletion_pending'].includes(errorCode(error) ?? '');
function retryRead(count: number, error: unknown) {
  const status = errorStatus(error);
  return count < 1 && !denied(error) && !['invalid_response','policy_unavailable','policy_version_changed'].includes(errorCode(error) ?? '') &&
    (error instanceof TypeError || status === 0 || status === 408 || status === 429 || status != null && status >= 500);
}
type ReadOptions<T> = {
  enabled?: boolean;
  subscribed?: boolean;
  public?: boolean;
  allowDeletionRead?: boolean;
  /** Same-session presentation data may remain visible during a fresh background read. */
  displayCached?: boolean;
  staleTime?: number;
  gcTime?: number;
  retry?: false;
  refetchOnWindowFocus?: boolean;
  refetchInterval?: number | false | ((query: Query<T>) => number | false);
};

/** Supplies account isolation/error semantics; Query is the sole read lifecycle. */
export function useAccountQuery<T>(key: string | readonly unknown[], loader: (signal: AbortSignal) => Promise<T>, language: Language = 'zh', options: ReadOptions<T> = {}) {
  const identity = useIdentity(), client = useQueryClient();
  const queryKey = [...identityKey(identity), ...(typeof key === 'string' ? [key] : key)];
  const keyHash = hashKey(queryKey);
  const stableKey = useMemo(() => queryKey, [keyHash]);
  const allowed = options.public === true || identity.privateEnabled && (!identity.deletionPending || options.allowDeletionRead === true && !!identity.owner);
  const enabled = options.enabled !== false && allowed;
  const queryOptions = { queryKey: stableKey, enabled,
    queryFn: ({ signal }: { signal: AbortSignal }) => { signal.throwIfAborted(); return loader(signal); },
    retry: options.retry === false ? false : retryRead, retryDelay: 500, refetchOnWindowFocus: options.refetchOnWindowFocus ?? false,
    staleTime: options.public ? options.staleTime ?? 15000 : 0, gcTime: options.gcTime ?? 60000,
    refetchInterval: options.refetchInterval, refetchIntervalInBackground: false,
  };
  const query = useQuery({ ...queryOptions, subscribed: options.subscribed });
  // Capability checks and task progression still require a fresh observer read.
  // Only opted-in presentation data can use this owner/session-scoped memory cache.
  const confirmed = useRef({ key: keyHash, readable: false });
  if (confirmed.current.key !== keyHash) confirmed.current = { key: keyHash, readable: false };
  if (query.isSuccess && query.isFetchedAfterMount) confirmed.current.readable = true;
  const scope = hashKey(identityKey(identity));
  const refresh = useCallback(() => options.displayCached && allowed && identity.isCurrent(scope)
    ? client.fetchQuery(queryOptions).then(() => {}, () => { /* Query retains the actual failure for the view. */ })
    : client.invalidateQueries({ queryKey: stableKey, exact: true }, { cancelRefetch: false }), [client, stableKey, allowed, options.displayCached, scope, identity.isCurrent]);
  const invalidate = useCallback(() => client.invalidateQueries({ queryKey: stableKey, exact: true }), [client, stableKey]);
  const read = () => enabled && identity.isCurrent(scope) ? client.fetchQuery(queryOptions) : Promise.reject(new Error('read_disabled'));
  // Apply an acknowledged business update to the sole cache, never recreate a
  // retired session's entry when a delayed write completes after navigation.
  const updateData = (updater: (previous: T | undefined) => T | undefined) => {
    if (!enabled || !identity.isCurrent(scope) || !client.getQueryState(stableKey)) return false;
    client.setQueryData<T>(stableKey, updater);
    return true;
  };
  const unreadable = denied(query.error) || options.displayCached && (errorStatus(query.error) === 404 || ['deleting','policy_unavailable','policy_version_changed'].includes(errorCode(query.error) ?? ''));
  const readable = allowed && (enabled || options.displayCached === true) && (options.public || confirmed.current.readable || options.displayCached && query.data !== undefined) && !unreadable;
  return { allowed, data: readable ? query.data ?? null : null,
    loading: enabled && (options.displayCached ? !readable && !query.isError : query.isPending || query.isFetching || !options.public && !confirmed.current.readable && !query.isError), error: query.error ? errorText(query.error, language) : '',
    errorCause: query.error, errorCode: errorCode(query.error), errorStatus: errorStatus(query.error), status: query.status, fetchStatus: query.fetchStatus, dataRevision: client.getQueryState(stableKey)?.dataUpdateCount ?? 0, refresh, invalidate, read, updateData };
}

/** Immutable files stay observed only while their admitted collection is mounted.
 * Unselected unfinished reads lose their observer and abort; completed reads
 * remain the sole in-memory data until the collection/owner/session retires.
 * The caller must freshly validate collection admission before retrying a denial. */
export function useAccountQueryFiles<T>(entries: { id: string; key: readonly unknown[]; active: boolean; load: (signal: AbortSignal) => Promise<T> }[], language: Language) {
  const identity = useIdentity(), client = useQueryClient(), scope = hashKey(identityKey(identity));
  const allowed = identity.privateEnabled && !identity.deletionPending;
  const candidates = allowed ? entries.map(entry => ({ ...entry, queryKey: [...identityKey(identity), ...entry.key] })) : [];
  const refused = candidates.some(entry => denied(client.getQueryState(entry.queryKey)?.error));
  const observed = candidates.filter(entry => entry.active || client.getQueryState(entry.queryKey)?.status === 'success' || denied(client.getQueryState(entry.queryKey)?.error));
  const results = useQueries({ queries: observed.map(entry => ({
    queryKey: entry.queryKey, enabled: !refused,
    queryFn: async ({ signal }: { signal: AbortSignal }) => {
      signal.throwIfAborted();
      if (!identity.isCurrent(scope)) throw new DOMException('The account scope was retired', 'AbortError');
      const value = await entry.load(signal);
      signal.throwIfAborted();
      if (!identity.isCurrent(scope)) throw new DOMException('The account scope was retired', 'AbortError');
      return value;
    },
    staleTime: Infinity, gcTime: 0, retry: retryRead, retryDelay: 500,
    refetchOnMount: false, refetchOnWindowFocus: false, refetchOnReconnect: false,
  })) });
  const unreadable = results.some(query => denied(query.error));
  return results.map((query, index) => ({ id: observed[index].id,
    data: allowed && !unreadable && errorStatus(query.error) !== 404 ? query.data ?? null : null,
    loading: query.isPending || query.isFetching,
    error: query.error ? errorText(query.error, language) : '', denied: denied(query.error),
    retry: () => identity.isCurrent(scope) && client.getQueryCache().find({ queryKey: observed[index].queryKey, exact: true })?.getObserversCount()
      ? denied(query.error) ? client.resetQueries({ queryKey: observed[index].queryKey, exact: true })
        : query.refetch({ cancelRefetch: false }) : Promise.resolve(),
  }));
}


/** Imperative task consumers borrow the provider's Query lifecycle, never another client. */
export function useAccountQueryObserver(): AccountQueryObserver {
  const identity = useIdentity(), client = useQueryClient(), scope = hashKey(identityKey(identity));
  const bridge = useMemo(() => {
    const pending = new Set<() => void>();
    const current = () => !!identity.owner && identity.privateEnabled && !identity.deletionPending && identity.isCurrent(scope);
    const abortError = () => new DOMException('The account observer was retired', 'AbortError');
    const api: AccountQueryObserver = {
      prefetch: async (key, loader) => {
        if (!current()) return;
        await client.prefetchQuery({ queryKey: [...identityKey(identity), ...key],
          queryFn: ({signal}) => { if (!current()) throw abortError(); signal.throwIfAborted(); return loader(signal); },
          // Suppress repeated pointer/focus intent; actual route reads still revalidate at staleTime 0.
          staleTime: 15000, gcTime: 60000, retry: retryRead, retryDelay: 500 });
      },
      seed: (key, data) => { if (current()) client.setQueryData([...identityKey(identity), ...key], data); },
      reset: key => { if (current()) void client.resetQueries({ queryKey: [...identityKey(identity), ...key], exact: true }); },
      observe: <T,>(key: readonly unknown[], loader: (signal: AbortSignal) => Promise<T>, signal: AbortSignal,
        options: { until?: (data: T) => boolean; onData?: (data: T) => void; interval?: number } = {}) => new Promise<T>((resolve, reject) => {
        if (signal.aborted || !current()) { reject(signal.reason ?? abortError()); return; }
        const observer = new QueryObserver<T>(client, { queryKey: [...identityKey(identity), ...key],
          queryFn: ({ signal: requestSignal }) => { requestSignal.throwIfAborted(); return loader(requestSignal); },
          retry: retryRead, retryDelay: 500, staleTime: 0, gcTime: 60000, refetchOnWindowFocus: true,
          refetchInterval: options.until ? query => query.state.status === 'error' || query.state.data !== undefined && options.until!(query.state.data) ? false : options.interval ?? 1500 : false,
          refetchIntervalInBackground: false,
        });
        let settled = false, unsubscribe = () => {};
        const finish = (error?: unknown, value?: T) => {
          if (settled) return;
          settled = true; unsubscribe(); observer.destroy(); pending.delete(retire); signal.removeEventListener('abort', abort);
          if (error !== undefined) reject(error); else resolve(value!);
        };
        const abort = () => finish(signal.reason ?? abortError()), retire = () => finish(abortError());
        pending.add(retire); signal.addEventListener('abort', abort, { once: true });
        unsubscribe = observer.subscribe(result => {
          if (!current()) { retire(); return; }
          if (!result.isFetchedAfterMount || result.fetchStatus !== 'idle') return;
          if (result.isError) { finish(result.error); return; }
          // An old terminal cache entry must not authorize progress, retry or completion.
          if (!result.isSuccess) return;
          try {
            options.onData?.(result.data);
            if (!settled && (!options.until || options.until(result.data))) finish(undefined, result.data);
          } catch (error) { finish(error); }
        });
        if (settled) unsubscribe();
      }),
      once: async (key, operation) => {
        const assertCurrent = () => { if (!current()) throw abortError(); };
        assertCurrent();
        const cache = client.getMutationCache(), mutationKey = [...identityKey(identity), ...key];
        const previous = cache.find({ mutationKey, exact: true, predicate: mutation => mutation.state.status === 'pending' || mutation.state.status === 'success' });
        const data = previous ? previous.state.status === 'success' ? previous.state.data : await previous.continue()
          : await cache.build(client, { mutationKey, mutationFn: () => { assertCurrent(); return operation(assertCurrent); }, retry: false }).execute(undefined);
        assertCurrent();
        return data;
      },
    };
    return { api, retire: () => { for (const cancel of pending) cancel(); } };
  }, [client, scope, identity.isCurrent]);
  useLayoutEffect(() => () => bridge.retire(), [bridge]);
  return bridge.api;
}

/** Mutations never retry implicitly. The lock/identity check protects UI commits. */
export function useAccountMutation(route: string, language: Language = 'zh') {
  const identity = useIdentity(), scope = hashKey([...identityKey(identity), route]);
  const current = useRef(scope), mounted = useRef(true), lock = useRef<string | null>(null);
  current.current = scope;
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  const mutation = useMutation({ mutationKey: [...identityKey(identity), route], retry: false,
    mutationFn: async ({ operation, success }: { operation: () => Promise<void>; success: string; scope: string }) => { await operation(); return success; },
  });
  // Guest request pages also use this lifecycle for their local login handoff.
  // Private writes still obtain a current token and pass server authorization.
  const isCurrent = () => (!identity.owner || identity.privateEnabled && !identity.deletionPending) && identity.isCurrent(hashKey(identityKey(identity))) && mounted.current && current.current === scope;
  const run = async (operation: () => Promise<void>, success = '') => {
    if (lock.current === scope || !isCurrent()) return;
    lock.current = scope;
    try { await mutation.mutateAsync({ operation, success, scope }); } catch { /* Query retains the actual failure; this handler matches UI event semantics. */ }
    finally { if (lock.current === scope) lock.current = null; }
  };
  const visible = mutation.variables?.scope === scope;
  return { busy: visible && mutation.isPending, error: visible && mutation.error ? errorText(mutation.error, language) : '',
    errorCode: visible ? errorCode(mutation.error) : undefined, notice: visible && mutation.isSuccess ? mutation.data : '',
    run, isCurrent, clear: mutation.reset };
}
